import React, { useState } from 'react';
import { generateCsvTemplate, bulkAddContacts } from '../../utils/portalStorage';
import { INITIAL_UZAIRUE_VILLAGES, INITIAL_DIASPORA_CHAPTERS, UzairueContact } from '../../data/initialContactsData';

interface ContactUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

interface ParsedRow {
  fullName: string;
  title: string;
  village: string;
  diasporaChapter: string;
  email: string;
  phone: string;
  whatsappEnabled: boolean;
  role: UzairueContact['role'];
  organization: string;
  notes: string;
  isValid: boolean;
}

export const ContactUploadModal: React.FC<ContactUploadModalProps> = ({
  isOpen,
  onClose,
  onImportSuccess,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importResult, setImportResult] = useState<{ importedCount: number; duplicateCount: number } | null>(null);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      processCsvFile(e.target.files[0]);
    }
  };

  const processCsvFile = (selectedFile: File) => {
    setFile(selectedFile);
    setIsProcessing(true);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (!text) {
        setIsProcessing(false);
        return;
      }

      // Simple CSV parsing
      const lines = text.split(/\r\n|\n/).filter((line) => line.trim().length > 0);
      if (lines.length < 2) {
        alert('CSV file appears to be empty or lacks header rows.');
        setIsProcessing(false);
        return;
      }

      // Parse headers
      const headers = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/['"]/g, ''));

      // Map columns
      const nameIdx = headers.findIndex((h) => h.includes('name') || h.includes('full'));
      const titleIdx = headers.findIndex((h) => h.includes('title') || h.includes('prefix'));
      const villageIdx = headers.findIndex((h) => h.includes('village') || h.includes('clan') || h.includes('sub'));
      const chapterIdx = headers.findIndex((h) => h.includes('diaspora') || h.includes('chapter') || h.includes('country'));
      const emailIdx = headers.findIndex((h) => h.includes('email') || h.includes('mail'));
      const phoneIdx = headers.findIndex((h) => h.includes('phone') || h.includes('mobile') || h.includes('tel'));
      const whatsappIdx = headers.findIndex((h) => h.includes('whatsapp') || h.includes('wa'));
      const roleIdx = headers.findIndex((h) => h.includes('role') || h.includes('position') || h.includes('designation'));
      const orgIdx = headers.findIndex((h) => h.includes('org') || h.includes('association') || h.includes('company'));
      const notesIdx = headers.findIndex((h) => h.includes('note') || h.includes('remark') || h.includes('comment'));

      const rows: ParsedRow[] = [];

      for (let i = 1; i < lines.length; i++) {
        // Handle comma within quotes briefly
        const rawRow = lines[i];
        const cols = rawRow.split(',').map((c) => c.trim().replace(/^["']|["']$/g, ''));

        if (cols.length === 0 || !cols.some((c) => c.length > 0)) continue;

        const fullName = nameIdx >= 0 && cols[nameIdx] ? cols[nameIdx] : cols[0] || 'Unknown Delegate';
        const title = titleIdx >= 0 && cols[titleIdx] ? cols[titleIdx] : '';
        const rawVillage = villageIdx >= 0 && cols[villageIdx] ? cols[villageIdx] : 'Jattu-Uzairue';
        const rawChapter = chapterIdx >= 0 && cols[chapterIdx] ? cols[chapterIdx] : 'Uzairue Local Govt / Edo State';
        const email = emailIdx >= 0 && cols[emailIdx] ? cols[emailIdx] : `delegate_${i}@uzairue.org`;
        const phone = phoneIdx >= 0 && cols[phoneIdx] ? cols[phoneIdx] : '';
        const whatsappRaw = whatsappIdx >= 0 && cols[whatsappIdx] ? cols[whatsappIdx].toLowerCase() : 'yes';
        const whatsappEnabled = whatsappRaw.includes('y') || whatsappRaw.includes('1') || whatsappRaw.includes('true');
        const roleRaw = roleIdx >= 0 && cols[roleIdx] ? cols[roleIdx] : 'General Diaspora Member';
        const organization = orgIdx >= 0 && cols[orgIdx] ? cols[orgIdx] : '';
        const notes = notesIdx >= 0 && cols[notesIdx] ? cols[notesIdx] : 'Bulk CSV Uploaded Contact';

        // Match village / chapter nicely
        const matchedVillage =
          INITIAL_UZAIRUE_VILLAGES.find((v) => v.toLowerCase().includes(rawVillage.toLowerCase())) ||
          INITIAL_UZAIRUE_VILLAGES[0];

        const matchedChapter =
          INITIAL_DIASPORA_CHAPTERS.find((c) => c.toLowerCase().includes(rawChapter.toLowerCase())) ||
          INITIAL_DIASPORA_CHAPTERS[0];

        let matchedRole: UzairueContact['role'] = 'General Diaspora Member';
        if (roleRaw.toLowerCase().includes('elder') || roleRaw.toLowerCase().includes('chief')) matchedRole = 'Royal Elder';
        else if (roleRaw.toLowerCase().includes('coordinator')) matchedRole = 'Festival Coordinator';
        else if (roleRaw.toLowerCase().includes('youth')) matchedRole = 'Youth Leader';
        else if (roleRaw.toLowerCase().includes('delegate')) matchedRole = 'Diaspora Delegate';

        rows.push({
          fullName,
          title,
          village: matchedVillage,
          diasporaChapter: matchedChapter,
          email,
          phone,
          whatsappEnabled,
          role: matchedRole,
          organization,
          notes,
          isValid: fullName.length > 1 && email.includes('@'),
        });
      }

      setParsedRows(rows);
      setIsProcessing(false);
    };

    reader.readAsText(selectedFile);
  };

  const handleConfirmImport = () => {
    const validContacts = parsedRows
      .filter((r) => r.isValid)
      .map((r) => ({
        fullName: r.fullName,
        title: r.title,
        village: r.village,
        diasporaChapter: r.diasporaChapter,
        email: r.email,
        phone: r.phone,
        whatsappEnabled: r.whatsappEnabled,
        role: r.role,
        organization: r.organization,
        verified: true,
        notes: r.notes,
      }));

    if (validContacts.length === 0) {
      alert('No valid contacts found in the uploaded CSV file.');
      return;
    }

    const res = bulkAddContacts(validContacts);
    setImportResult(res);
    onImportSuccess();
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-amber-500/40 w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] text-white">
        {/* Header */}
        <div className="bg-slate-950 border-b border-slate-800 p-5 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📥</span>
            <div>
              <h2 className="text-base font-black font-serif text-amber-300 uppercase tracking-wide">
                Bulk Upload Uzairue Diaspora Contacts
              </h2>
              <p className="text-xs text-slate-400">
                Upload contact details for Uzairue sons & daughters worldwide
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-red-600 text-slate-300 hover:text-white transition-all cursor-pointer font-bold text-xs"
          >
            ✕ Close
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {/* Step 1: Download Standard Template Banner */}
          <div className="bg-emerald-950/80 border border-emerald-700/80 p-4 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h4 className="text-xs font-bold text-emerald-300 uppercase tracking-wider flex items-center gap-1.5">
                <span>📄</span> Official Uzairue CSV Contact Template
              </h4>
              <p className="text-[11px] text-slate-300 mt-1">
                Download the formatted template with predefined headers (Name, Title, Village, Diaspora Chapter, Email, Phone, Role) to prepare your file.
              </p>
            </div>
            <button
              onClick={generateCsvTemplate}
              className="bg-amber-400 hover:bg-amber-300 text-slate-950 font-extrabold text-xs px-4 py-2 rounded-lg shadow transition-all cursor-pointer whitespace-nowrap shrink-0 flex items-center gap-1.5"
            >
              <span>⬇️</span> Download Template (.csv)
            </button>
          </div>

          {/* Step 2: Upload Dropzone */}
          <div className="border-2 border-dashed border-slate-700 hover:border-amber-400 bg-slate-950/60 p-6 rounded-xl text-center space-y-3 transition-colors cursor-pointer relative">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <div className="w-12 h-12 rounded-full bg-slate-800 border border-slate-700 mx-auto flex items-center justify-center text-2xl text-amber-400">
              📂
            </div>
            <div>
              <p className="text-sm font-bold text-slate-200">
                {file ? `Selected File: ${file.name}` : 'Click or Drag & Drop Uzairue CSV Contacts File'}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">Supports comma-separated (.csv) files up to 10MB</p>
            </div>
          </div>

          {/* Import Result Feedback Banner */}
          {importResult && (
            <div className="bg-emerald-900/90 border border-emerald-400 p-4 rounded-xl text-xs space-y-1 text-emerald-200 animate-fadeIn">
              <div className="font-bold text-amber-300 text-sm flex items-center gap-1">
                <span>✅</span> Bulk Upload Completed Successfully!
              </div>
              <p>
                • <strong>{importResult.importedCount}</strong> new contacts added to the global directory.
              </p>
              {importResult.duplicateCount > 0 && (
                <p className="text-amber-200">
                  • <strong>{importResult.duplicateCount}</strong> duplicate records skipped automatically.
                </p>
              )}
            </div>
          )}

          {/* Step 3: Parsed Rows Preview */}
          {parsedRows.length > 0 && !importResult && (
            <div className="space-y-3">
              <div className="flex justify-between items-center text-xs font-bold text-amber-300">
                <span>Preview Parsed Records ({parsedRows.length})</span>
                <span className="text-slate-400">
                  {parsedRows.filter((r) => r.isValid).length} Valid for Import
                </span>
              </div>

              <div className="border border-slate-800 rounded-xl overflow-x-auto max-h-56 bg-slate-950">
                <table className="w-full text-left text-[11px]">
                  <thead className="bg-slate-800 text-slate-300 uppercase font-mono text-[10px] sticky top-0">
                    <tr>
                      <th className="p-2">Name</th>
                      <th className="p-2">Village</th>
                      <th className="p-2">Diaspora Chapter</th>
                      <th className="p-2">Email</th>
                      <th className="p-2">Phone</th>
                      <th className="p-2">Role</th>
                      <th className="p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800 text-slate-300">
                    {parsedRows.map((r, idx) => (
                      <tr key={idx} className={r.isValid ? 'hover:bg-slate-900' : 'bg-red-950/40 text-red-300'}>
                        <td className="p-2 font-bold text-white whitespace-nowrap">
                          {r.title ? `${r.title} ` : ''}
                          {r.fullName}
                        </td>
                        <td className="p-2 whitespace-nowrap">{r.village}</td>
                        <td className="p-2 whitespace-nowrap">{r.diasporaChapter}</td>
                        <td className="p-2 font-mono whitespace-nowrap">{r.email}</td>
                        <td className="p-2 whitespace-nowrap">{r.phone || 'N/A'}</td>
                        <td className="p-2 whitespace-nowrap">{r.role}</td>
                        <td className="p-2">
                          {r.isValid ? (
                            <span className="text-emerald-400 font-bold">Valid</span>
                          ) : (
                            <span className="text-red-400 font-bold">Invalid</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="bg-slate-950 border-t border-slate-800 p-4 flex justify-between items-center shrink-0">
          <button
            onClick={generateCsvTemplate}
            className="text-xs text-amber-400 hover:underline font-bold flex items-center gap-1 cursor-pointer"
          >
            <span>📥</span> Download CSV Template
          </button>

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-lg cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmImport}
              disabled={parsedRows.length === 0 || isProcessing || importResult !== null}
              className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-extrabold rounded-lg shadow-md cursor-pointer flex items-center gap-1.5"
            >
              <span>🚀</span> Confirm & Import {parsedRows.filter((r) => r.isValid).length} Contacts
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUploadModal;
